import { useEffect, useRef } from "react";

import { getVerboseError } from "@shared/libs/oh-errors/error-formatter";
import { OhApi } from "@shared/libs/siayuda";
import { toastApi } from "@shared/libs/toasts";
/* eslint-disable no-restricted-imports */
// This is the ONLY place where we import React Query hooks directly
// Everywhere else in the codebase, we use the generators defined here
// to override the default RQ behavior
import {
  InfiniteData,
  keepPreviousData as keepPreviousDataFn,
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery as useInfiniteRQQuery,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation as useRQMutation,
  useQueries as useRQQueries,
  useQuery as useRQQuery,
} from "@tanstack/react-query";

import { listQueryKey } from "./query-keys.constants";
import { rerouteApiError } from "./query.helpers";
import {
  InfiniteQueryProps,
  PaginatedQueryParams,
  PaginatedQueryResponse,
  QueryResultWithKey,
} from "./query.type";

// ---------------------------------------------------------------------------
// ---- React Query v5 back-compat shim --------------------------------------
// ---------------------------------------------------------------------------
// React Query v5 removed `onSuccess`/`onError`/`onSettled` from queries and the
// `keepPreviousData` boolean option, and renamed mutation `isLoading` to
// `isPending`. To keep the v4 → v5 migration diff localized to this file, we
// re-implement those removed APIs here and mark them as @deprecated so no NEW
// usage is introduced. Existing usages should be migrated off progressively,
// after which this shim can be deleted.

/**
 * Query lifecycle callbacks removed from `useQuery`/`useInfiniteQuery` in v5.
 * Re-implemented locally via a `useEffect` keyed on the query's update timestamps.
 */
type DeprecatedQueryCallbacks<Data, Err> = {
  /** @deprecated v5 removed query `onSuccess`. Move the logic to a `useEffect` watching `data`, then delete this prop. */
  onSuccess?: (data: Data) => void;
  /** @deprecated v5 removed query `onError`. Move the logic to a `useEffect` watching `error`, then delete this prop. */
  onError?: (error: Err) => void;
  /** @deprecated v5 removed query `onSettled`. Move the logic to a `useEffect` watching `data`/`error`, then delete this prop. */
  onSettled?: (data: Data | undefined, error: Err | null) => void;
};

/**
 * The `keepPreviousData` boolean was removed in v5.
 */
type DeprecatedKeepPreviousData = {
  /** @deprecated v5 removed the `keepPreviousData` boolean. Use `placeholderData: keepPreviousData` (imported from @tanstack/react-query) instead, then delete this prop. */
  keepPreviousData?: boolean;
};

type LegacyQueryOptions<Data, Err> = Omit<UseQueryOptions<Data, Err>, "queryKey" | "queryFn"> &
  DeprecatedQueryCallbacks<Data, Err> &
  DeprecatedKeepPreviousData;

// minimal structural shape shared by query and infinite-query results
type Settleable<Data, Err> = Pick<
  UseQueryResult<Data, Err>,
  "isSuccess" | "isError" | "data" | "error" | "dataUpdatedAt" | "errorUpdatedAt"
>;

/**
 * Splits the deprecated options (callbacks + keepPreviousData) out of the option
 * bag and translates `keepPreviousData` into the v5 `placeholderData` API.
 */
const splitLegacyOptions = <Data, Err>(options?: LegacyQueryOptions<Data, Err>) => {
  const { onSuccess, onError, onSettled, keepPreviousData, ...rest } = options ?? {};
  const queryOptions = {
    ...rest,
    ...(keepPreviousData ? { placeholderData: keepPreviousDataFn } : {}),
  } as Omit<UseQueryOptions<Data, Err>, "queryKey" | "queryFn">;
  return { queryOptions, onSuccess, onError, onSettled };
};

/**
 * Re-implements the removed query `onSuccess`/`onError`/`onSettled` callbacks.
 * Fires once per settled fetch, detected via `dataUpdatedAt`/`errorUpdatedAt`
 * changes (so it does not re-fire on unrelated re-renders).
 */
const useLegacyQueryCallbacks = <Data, Err>(
  result: Settleable<Data, Err>,
  { onSuccess, onError, onSettled }: DeprecatedQueryCallbacks<Data, Err>
) => {
  const lastDataUpdatedAt = useRef(0);
  const lastErrorUpdatedAt = useRef(0);

  useEffect(() => {
    if (result.isSuccess && result.dataUpdatedAt !== lastDataUpdatedAt.current) {
      lastDataUpdatedAt.current = result.dataUpdatedAt;
      onSuccess?.(result.data as Data);
      onSettled?.(result.data, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.isSuccess, result.dataUpdatedAt]);

  useEffect(() => {
    if (result.isError && result.errorUpdatedAt !== lastErrorUpdatedAt.current) {
      lastErrorUpdatedAt.current = result.errorUpdatedAt;
      onError?.(result.error as Err);
      onSettled?.(undefined, result.error as Err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.isError, result.errorUpdatedAt]);
};

/**
 * A thin wrapper around useQuery for queries that bypass the OH-specific error
 * handling pipeline (e.g. Stream Chat). Callers get standard React Query
 * behavior without the rerouteApiError / OhApi.Failure machinery.
 */
export const useExternalQuery = <Data, Err = Error>(
  queryKey: QueryKey,
  queryFn: () => Promise<Data>,
  options?: Omit<UseQueryOptions<Data, Err>, "queryKey" | "queryFn">
): UseQueryResult<Data, Err> => {
  return useRQQuery<Data, Err>({
    ...options,
    queryKey, 
    queryFn, 
    // Structural opt-out from the OH rate-limit global pause (see query-client.ts).
    // Forced here so call-sites cannot accidentally re-enroll an external
    // backend into the OH-only pause mechanism.
    meta: { ...options?.meta, external: true },
  });
};

/**
 * an overload of react-query's useQuery hook which add specific logic
 * related to how we handle errors in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useQuery hook
 */
export const queryGenerator =
  <T extends readonly unknown[], Data, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  (
    data: T,
    options?: LegacyQueryOptions<Data, Err>
  ): QueryResultWithKey<UseQueryResult<Data, Err>> => {
    // construct query key beforehand to expose it
    const queryKey = [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), data];
    const { queryOptions, onSuccess, onError, onSettled } = splitLegacyOptions(options);
    const result = useRQQuery<Data, Err>({
      queryKey,
      queryFn: () => rerouteApiError(requestFn(...data)).then(r => r.value),
      ...queryOptions,
    });
    useLegacyQueryCallbacks(result, { onSuccess, onError, onSettled });
    return {
      queryKey,
      ...result,
    };
  };

/**
 * an overload of react-query's useQueries hook which add specific logic
 * related to how we handle errors in HB
 * BEHOLD: contrary to standard useQueries, only allow multiple fetch of same data type
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useQuery hook
 */
export const queriesGenerator =
  <T extends readonly unknown[], Data, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  (data: T[], options?: Omit<UseQueryOptions<Data, Err>, "queryKey" | "queryFn">) =>
    useRQQueries({
      queries: data.map(queryData => ({
        queryKey: [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), queryData],
        queryFn: () => rerouteApiError(requestFn(...queryData)).then(r => r.value),
        ...options,
      })),
    }) as UseQueryResult<Data, Err>[];

// global toggle for mutation notifications related to global errors
let IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = true;
export const enableAutomaticGlobalError = () => (IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = true);
export const disableAutomaticGlobalError = () => (IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = false);

/**
 * Mutation result with the v4 `isLoading` flag re-added on top of the v5 result.
 */
type LegacyMutationResult<Data, Err, T> = UseMutationResult<Data, Err, T> & {
  /** @deprecated v5 renamed mutation `isLoading` to `isPending`. Use `isPending` instead, then delete this usage. */
  isLoading: boolean;
};

/**
 * an overload of react-query's useMutation hook which add specific logic
 * related to how we handle errors in HB
 * @param requestFn function which triggers our edition through network call
 * @returns an overloaded react query useMutation hook
 */
export const mutationGenerator =
  <T extends readonly unknown[], Data, Err extends OhApi.Failure = OhApi.Failure>(
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  (options?: UseMutationOptions<Data, Err, T>): LegacyMutationResult<Data, Err, T> => {
    const result = useRQMutation<Data, Err, T>({
      mutationFn: (data: T) => rerouteApiError(requestFn(...data)).then(r => r.value),
      ...options,
      onError: (err, variables, onMutateResult, context) => {
        if (OhApi.isFetchError(err) || OhApi.isTimeoutError(err)) {
          toastApi.add({
            variant: "error",
            duration: 10_000,
            title: "Une erreur de connexion s’est produite",
            message:
              "Cela peut être dû à un délai d'attente ou à une connexion instable. " +
              "Voici quelques solutions : vérifiez votre connexion Internet, rechargez " +
              "la page ou réessayez plus tard. Si le problème persiste contactez-nous.",
          });
        }
        if (OhApi.isGlobalError(err) && IS_AUTOMATIC_GLOBAL_ERROR_ENABLE) {
          toastApi.add({
            variant: "error",
            title: "Une erreur s'est produite",
            message: getVerboseError(err.error, err.extra),
          });
        }
        if (OhApi.isUnrecoverableHttpError(err) && err.statusCode === 403) {
          toastApi.add({
            variant: "error",
            title: "Une erreur s'est produite",
            message: "Vous n'avez pas les droits pour effectuer cette action",
          });
        } else if (OhApi.isUnrecoverableHttpError(err) && err.statusCode === 401) {
          toastApi.add({
            variant: "error",
            title: "Une erreur s'est produite",
            message: "Vous n'êtes pas autorisé à effectuer cette action",
          });
        } else if (OhApi.isUnrecoverableHttpError(err)) {
          toastApi.add({
            variant: "error",
            title: "Une erreur s'est produite",
            message:
              "Cela peut être dû à un problème serveur. " +
              "Voici quelques solutions : vérifiez votre connexion Internet, rechargez " +
              "la page ou réessayez plus tard. Si le problème persiste contactez-nous.",
          });
        }
        options?.onError?.(err, variables, onMutateResult, context);
      },
    });

    // @deprecated back-compat alias for the v4 `isLoading` flag (renamed `isPending` in v5)
    return { ...result, isLoading: result.isPending };
  };

/**
 * an overload of react-query's useInfiniteQuery hook which add specific logic
 * related to how we handle errors and paginated query filters in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call which filterType input :
 * - filter
 * - sort
 * - pager
 * @returns an overloaded react query useInfiniteQuery hook
 */
export const infinitePaginatedQueryGenerator =
  <Filters, Datum, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (props: PaginatedQueryParams<Filters>) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (data: InfiniteQueryProps<Filters>) =>
    useInfiniteRQQuery<
      PaginatedQueryResponse<Datum>,
      Err,
      InfiniteData<PaginatedQueryResponse<Datum>>,
      QueryKey,
      { page: number }
    >({
      // keys related to queryBaseName and incoming filters
      queryKey: [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), "list", data],
      queryFn: ({ pageParam }) =>
        rerouteApiError(
          requestFn({
            // rebuild filter, sort and pager data
            filters: data.filters,
            sort: data.sort,
            pager: { size: data.size, page: pageParam?.page ?? 1 },
          })
        ).then(resp => ({
          // extract pager and data from response
          pager: resp.pager,
          items: resp.value,
        })),
      initialPageParam: { page: 1 },
      // retrieve next and previous page data from current response data
      getNextPageParam: ({ pager }) =>
        pager && pager.page !== pager.pages ? { page: pager.page + 1 } : undefined,
      getPreviousPageParam: ({ pager }) =>
        pager && pager.page > 0 ? { page: pager.page - 1 } : undefined,
    });

/**
 * an overload of react-query's useInfiniteQuery hook which add specific logic
 * related to how we handle errors and cursored query filters in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useInfiniteQuery hook
 */
// `getNextPageParam` is required by react-query and is supplied by the caller for
// cursored queries, so options (and that field) are mandatory here.
type InfiniteCursoredQueryOptions<Filters extends any[], Datum, Err> = Omit<
  UseInfiniteQueryOptions<Datum[], Err, InfiniteData<Datum[]>, QueryKey, Filters>,
  "queryKey" | "queryFn" | "initialPageParam"
> &
  DeprecatedQueryCallbacks<InfiniteData<Datum[]>, Err>;

export const infiniteCursoredQueryGenerator =
  <Filters extends any[], Datum, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...props: Filters) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (initialFilters: Filters, options: InfiniteCursoredQueryOptions<Filters, Datum, Err>) => {
    const queryKey = [
      ...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]),
      "list",
      initialFilters,
    ];
    const { onSuccess, onError, onSettled, ...queryOptions } = options;
    const result = useInfiniteRQQuery<Datum[], Err, InfiniteData<Datum[]>, QueryKey, Filters>({
      queryKey,
      queryFn: ({ pageParam }) =>
        rerouteApiError(requestFn(...((pageParam ?? initialFilters) as Filters))).then(
          resp => resp.value
        ),
      initialPageParam: initialFilters,
      ...queryOptions,
    });
    useLegacyQueryCallbacks(result, { onSuccess, onError, onSettled });
    return {
      queryKey,
      ...result,
    };
  };

/**
 * an overload of react-query's useQuery hook which add specific logic.
 * returned data is structured as { items: In[], pager?: OhAPI.Pager }
 * giving us access to pagination data
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call with filterType input :
 * - filter
 * - sort
 * - pager
 * @returns an overloaded react query useQuery hook
 */

export const paginatedQueryGenerator =
  <Filters, Datum, Err = OhApi.Failure>(
    queryBaseName: string,
    requestFn: (params: PaginatedQueryParams<Filters>) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (
    params: PaginatedQueryParams<Filters>,
    options?: LegacyQueryOptions<PaginatedQueryResponse<Datum>, Err>
  ) => {
    const { queryOptions, onSuccess, onError, onSettled } = splitLegacyOptions(options);
    const result = useRQQuery<PaginatedQueryResponse<Datum>, Err>({
      queryKey: [queryBaseName, listQueryKey, params],
      queryFn: () =>
        rerouteApiError(requestFn(params)).then(resp => ({
          items: resp.value,
          pager: resp.pager,
        })),
      ...queryOptions,
    });
    useLegacyQueryCallbacks(result, { onSuccess, onError, onSettled });
    return result;
  };
